import { apm } from "@elastic/apm-rum";
import { ContentCopy, PushPin, PushPinOutlined } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";

interface Area {
  id: string;
  label: string;
  value: string;
  accuarcy: string;
}

export default function AreaContent() {
  const darkTheme = defaultStore((state) => state.darkTheme);
  const changeAlertMessage = defaultStore((state) => state.changeAlertMessage);

  const areaToolValues = sidebarStore((state) => state.areaToolValues);
  const showSmallToolContent = sidebarStore(
    (state) => state.showSmallToolContent
  );
  const areaActiveAttributes = sidebarStore(
    (state) => state.areaActiveAttributes
  );
  const changeAreaActiveAttributes = sidebarStore(
    (state) => state.changeAreaActiveAttributes
  );

  const { t } = useTranslation();

  const defaultValue = "0.00";
  const [area3D, set3DArea] = useState<string>(defaultValue);
  const [area2D, set2DArea] = useState<string>(defaultValue);
  const [area3DPrec, set3DAreaPrec] = useState<string>(defaultValue);
  const [area2DPrec, set2DAreaPrec] = useState<string>(defaultValue);

  //copy value from input
  const copyValue = (value: string) => {
    navigator.clipboard.writeText(value);
    changeAlertMessage({
      message: t("alert_success.copying_of_values"),
      type: "success",
    });
  };

  //pin value for small content
  const pinValue = (value: string) => {
    if (areaActiveAttributes.includes(value)) {
      changeAreaActiveAttributes(
        areaActiveAttributes.filter((item: string) => item !== value)
      );
    } else {
      changeAreaActiveAttributes([...areaActiveAttributes, value]);
    }
  };

  //attributes list
  const attributes: Area[] = [
    { id: "area3d", label: t("area.3d"), value: area3D, accuarcy: area3DPrec },
    { id: "area2d", label: t("area.2d"), value: area2D, accuarcy: area2DPrec },
  ];

  //update states for 2d and 3d areas
  useEffect(() => {
    if (areaToolValues !== undefined) {
      set3DArea(areaToolValues.area.toFixed(2));
      set2DArea(areaToolValues.area2d.toFixed(2));
      set3DAreaPrec(areaToolValues.precArea.toFixed(2));
      set2DAreaPrec(areaToolValues.precArea2d.toFixed(2));
    } else {
      set3DArea(defaultValue);
      set2DArea(defaultValue);
      set3DAreaPrec(defaultValue);
      set2DAreaPrec(defaultValue);
    }
  }, [areaToolValues]);

  return (
    <Box
      width="100%"
      display={"flex"}
      alignItems={"start"}
      flexDirection={"column"}
      gap={0.5}
    >
      {attributes
        .filter((item: Area) => {
          if (showSmallToolContent) {
            return areaActiveAttributes.includes(item.id);
          } else {
            return item;
          }
        })
        .map((item: Area) => (
          <Box
            key={item.label}
            width={"100%"}
            display={"flex"}
            alignItems={"start"}
            flexDirection={"column"}
            gap={0.5}
          >
            <Typography
              fontSize={16}
              color={darkTheme ? "white" : "darkblue.main"}
              fontWeight={700}
              noWrap
            >
              {item.label}
            </Typography>
            <Box width={"100%"} display={"flex"} alignItems={"center"}>
              <Box
                width={showSmallToolContent ? "100%" : "calc(100% - 34px)"}
                display={"flex"}
                justifyContent={"space-between"}
                alignItems={"center"}
                border={1}
                borderColor={darkTheme ? "white" : "darkblue.main"}
                borderRadius={1}
                gap={2}
              >
                <Typography
                  variant="body2"
                  fontWeight={400}
                  paddingX={0.5}
                  color={darkTheme ? "white" : "darkblue.main"}
                  noWrap
                >
                  {item.value} m<sup>2</sup>
                  {/* What does this name mean? 10 */}
                  {/* {item.value} m<sup>2</sup> ± {item.accuarcy} m<sup>2</sup> */}
                </Typography>
                <IconButton
                  disableRipple
                  disableFocusRipple
                  onClick={() =>
                    monitor(apm, "copy-value | Area", () =>
                      copyValue(item.value)
                    )
                  }
                  size="small"
                  sx={{
                    color: darkTheme ? "white" : "darkblue.main",
                    "@media (hover: hover)": {
                      ":hover": { color: "secondary.main" },
                    },
                  }}
                >
                  <ContentCopy />
                </IconButton>
              </Box>
              {!showSmallToolContent && (
                <IconButton
                  disableRipple
                  disableFocusRipple
                  onClick={() =>
                    monitor(apm, "change-pin-status | Area", () =>
                      pinValue(item.id)
                    )
                  }
                  size="small"
                  sx={{
                    color: darkTheme ? "white" : "darkblue.main",
                    "@media (hover: hover)": {
                      ":hover": { color: "secondary.main" },
                    },
                  }}
                >
                  {areaActiveAttributes.includes(item.id) ? (
                    <PushPin />
                  ) : (
                    <PushPinOutlined />
                  )}
                </IconButton>
              )}
            </Box>
          </Box>
        ))}
    </Box>
  );
}
